import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ClickAwayListener } from '@material-ui/core';
import { SmsSender } from '~/types/accountInfo';
import { ArrowUpIcon } from '~/assets/svgIcons';
import { useSelector } from 'react-redux';
import { RootState } from '~/store/config';
import {
  ArrowDropDown,
  Button,
  SelectedSender,
  Container,
  Menu,
  MenuItem,
  MenuList,
} from './styles';

const MAX_INPUT_VISIBLE_SENDERS = 2;

type SenderSelectorProps = {
  onChange: (sender: SmsSender) => void;
  sender: SmsSender | null;
  /**
   * If provided, the selector will be disabled and will show these senders as selected.
   * This is useful when a template with pre-approved senders is selected.
   */
  defaultSenders?: string[];
};

export default function SenderSelector({
  onChange,
  sender,
  defaultSenders,
}: SenderSelectorProps) {
  // REF's
  const menuRef = useRef(null);

  // STATES
  const [open, setOpen] = useState<boolean>(false);

  // SELECTORS
  const { smsSenders: sendersList } = useSelector(
    (state: RootState) => state.accountInfo
  );

  // HOOKS
  const { t } = useTranslation();

  // CONST's
  const isDisabled = defaultSenders?.length;
  const allowGrowOnHover =
    defaultSenders && defaultSenders?.length > MAX_INPUT_VISIBLE_SENDERS;

  const handleSenderChange = (e: SmsSender) => {
    setOpen(false);
    if (onChange) onChange(e);
  };

  const renderSelectedSenders = () => {
    return defaultSenders?.length
      ? defaultSenders.join(', ')
      : sender?.name || t('screens.conversation.senderSelector.chooseSenders');
  };

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Container>
        <Menu ref={menuRef} data-test-id="sender-selector">
          <Button
            disabled={isDisabled}
            onClick={() => setOpen(!open)}
            open={open}
            title={renderSelectedSenders()}>
            <SelectedSender allowGrowOnHover={allowGrowOnHover}>
              {renderSelectedSenders()}
            </SelectedSender>
            {!isDisabled && <ArrowDropDown open={open} src={ArrowUpIcon} />}
          </Button>
          <MenuList open={open}>
            {sendersList.map(e => (
              <MenuItem
                role="button"
                data-test-id={`sender-option-${e.name}`}
                key={String(e.name)}
                onClick={() => handleSenderChange(e)}
                isSelected={sender?.name === e.name}>
                {e.name}
              </MenuItem>
            ))}
          </MenuList>
        </Menu>
      </Container>
    </ClickAwayListener>
  );
}

SenderSelector.defaultProps = {
  defaultSenders: [],
};
